import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Bank as BankIcon, ArrowRight, Paperclip, Eye, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { STAGE_LABELS, STAGE_ORDER, STAGE_COLORS, BANK_CATEGORIES, EMPANELMENT_STATUS, bankKey } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DatePicker } from "@/components/DatePicker";
import { BankCombobox } from "@/components/BankCombobox";

const EMPTY = { bank_name: "", branch_name: "", address: "", contact_person: "", designation: "", phone: "", email: "", stage: "prospect", potential_value: "", notes: "", ifsc: "", branch_code: "", region: "", bank_category: "Unclassified", relationship_manager: "", empanelment_status: "not_empanelled", empanelment_category: "", empanelment_date: "", empanelment_renewal_due: "", report_format_ids: [] };
const LAYOUT_LABELS = { open_land: "Open Land", land_building: "Land & Building", industrial_land: "Industrial Land", flat: "Flats" };

export default function Branches() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [params] = useSearchParams();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [empFilter, setEmpFilter] = useState("all");
  const [gisPrefs, setGisPrefs] = useState([]);
  const [formats, setFormats] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [tplMap, setTplMap] = useState({});
  const [tplTarget, setTplTarget] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);
  const tplFileRef = useRef(null);
  const tplKey = (bank, fmtId) => `${bankKey(bank)}||${fmtId}`;
  const loadTpls = () => api.get("/bank-templates/all").then((r) => {
    const m = {}; (r.data || []).forEach((t) => { m[tplKey(t.bank_name, t.report_format_id)] = t; });
    setTplMap(m);
  }).catch(() => {});
  const openTpl = (bank, f) => setTplTarget({ bank, fmtId: f.id, fmtName: f.name, layout: LAYOUT_LABELS[f.layout_key] || f.layout_key });
  const uploadTpl = async (e) => {
    const file = e.target.files?.[0]; if (!file || !tplTarget) return;
    const fd = new FormData(); fd.append("file", file);
    setTplBusy(true);
    try {
      await api.post("/bank-templates/template", fd, { params: { bank: tplTarget.bank, format_id: tplTarget.fmtId } });
      toast.success("Template attached");
      await loadTpls();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTplBusy(false); if (tplFileRef.current) tplFileRef.current.value = ""; }
  };
  const removeTpl = async () => {
    if (!tplTarget) return;
    setTplBusy(true);
    try {
      await api.delete("/bank-templates/template", { params: { bank: tplTarget.bank, format_id: tplTarget.fmtId } });
      toast.success("Template removed");
      await loadTpls();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTplBusy(false); }
  };
  const toggleFormat = (id) => setForm((f) => { const cur = f.report_format_ids || []; return { ...f, report_format_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });

  const SRC_LABEL = { satellite: "Satellite", hybrid: "Satellite + labels", street: "Street (OSM)" };
  const loadGisPrefs = () => api.get("/gis/branch-prefs/all").then((r) => setGisPrefs(r.data || [])).catch(() => {});
  const resetGisPref = async (p) => {
    try {
      await api.delete("/gis/branch-prefs", { params: { bank: p.bank, branch: p.branch || "" } });
      toast.success("GIS defaults reset");
      loadGisPrefs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { loadGisPrefs(); }, []);

  const load = () => api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/report-formats").then((r) => setFormats((r.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {}); api.get("/banks").then((r) => setBankList(r.data || [])).catch(() => {}); loadTpls(); }, []);
  useEffect(() => { if (params.get("new")) { setEditing(null); setForm(EMPTY); setOpen(true); } }, [params]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ ...EMPTY, ...b, potential_value: b.potential_value || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    if (!form.bank_name || !form.bank_name.trim()) { toast.error("Please select or add a bank name"); return; }
    try {
      const payload = { ...form, potential_value: parseFloat(form.potential_value) || 0 };
      if (editing) await api.put(`/branches/${editing}`, payload);
      else await api.post("/branches", payload);
      const nb = (form.bank_name || "").trim();
      if (nb) setBankList((prev) => prev.some((b) => b.toLowerCase() === nb.toLowerCase()) ? prev : [...prev, nb].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      toast.success(editing ? "Branch updated" : "Branch added");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/branches/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = branches.filter((b) => {
    const q = search.toLowerCase();
    const matchQ = !q || `${b.bank_name} ${b.branch_name} ${b.contact_person} ${b.address} ${b.ifsc || ""} ${b.region || ""}`.toLowerCase().includes(q);
    const matchS = stageFilter === "all" || b.stage === stageFilter;
    const matchR = regionFilter === "all" || (b.region || "Unassigned") === regionFilter;
    const matchE = empFilter === "all" || (b.empanelment_status || "not_empanelled") === empFilter;
    return matchQ && matchS && matchR && matchE;
  });

  const regions = [...new Set(branches.map((b) => b.region || "Unassigned"))].sort();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">CRM</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Bank Branches</h1>
          <p className="text-sm text-muted-foreground mt-1">{branches.length} branches tracked</p>
        </div>
        <Button onClick={openNew} data-testid="add-branch-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> Add Branch</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bank, branch, contact…" className="pl-9" data-testid="branch-search" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[170px]" data-testid="stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[170px]" data-testid="region-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Regions</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="w-[190px]" data-testid="empanelment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Empanelments</SelectItem>
            {Object.entries(EMPANELMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {gisPrefs.length > 0 && (
        <Card className="p-5 border-border" data-testid="gis-defaults-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">GIS Studio</div>
              <h3 className="font-display font-semibold text-lg text-primary">Remembered Map Defaults</h3>
              <p className="text-xs text-muted-foreground">Preferred satellite zoom &amp; base map per bank branch, applied automatically in the GIS Overlay tab.</p>
            </div>
          </div>
          <div className="divide-y divide-border">
            {gisPrefs.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 py-2" data-testid={`gis-pref-${idx}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-primary truncate">{p.bank}{p.branch ? <span className="text-muted-foreground font-normal"> — {p.branch}</span> : null}</div>
                  <div className="text-[11px] text-muted-foreground">{p.gis_zoom ? `Zoom ${p.gis_zoom}` : "Default zoom"} · {SRC_LABEL[p.gis_source] || "Satellite"}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-xs shrink-0" onClick={() => resetGisPref(p)} data-testid={`gis-pref-reset-${idx}`}>Reset</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((b) => (
          <Card key={b.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`branch-card-${b.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><BankIcon size={18} weight="duotone" className="text-primary shrink-0" /><h3 className="font-display font-semibold text-lg text-primary truncate">{b.bank_name}</h3></div>
                <div className="text-sm text-muted-foreground">{b.branch_name}</div>
                {(b.region || b.ifsc) && <div className="text-[11px] text-muted-foreground mt-0.5">{[b.region, b.ifsc].filter(Boolean).join(" · ")}</div>}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${STAGE_COLORS[b.stage]}`}>{STAGE_LABELS[b.stage]}</span>
            </div>
            {b.address && <div className="text-xs text-muted-foreground mt-2">{b.address}</div>}
            {(b.report_format_ids || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid={`branch-formats-${b.id}`}>
                {[...(b.report_format_ids || [])].sort((a, c) => {
                  const fa = formats.find((x) => x.id === a); const fc = formats.find((x) => x.id === c);
                  return (fa?.name || "").localeCompare(fc?.name || "");
                }).map((id) => {
                  const f = formats.find((x) => x.id === id);
                  if (!f) return null;
                  const attached = !!tplMap[tplKey(b.bank_name, id)];
                  return <button key={id} type="button" onClick={() => openTpl(b.bank_name, f)} title={attached ? `Template attached — ${tplMap[tplKey(b.bank_name, id)].template_filename}` : "Attach template for draft / final prints"}
                    data-testid={`branch-format-link-${b.id}-${id}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap border transition-colors ${attached ? "bg-primary/10 text-primary border-primary/40 hover:bg-primary/20" : "bg-accent/10 text-amber-800 border-accent/30 hover:border-primary hover:text-primary"}`}>
                    <Paperclip size={11} weight={attached ? "fill" : "regular"} />{LAYOUT_LABELS[f.layout_key] || f.layout_key}</button>;
                })}
              </div>
            )}
            {(b.contact_person || b.phone) && (
              <div className="mt-3 text-sm">
                {b.contact_person && <div className="font-medium text-primary">{b.contact_person}{b.designation && <span className="text-muted-foreground font-normal"> · {b.designation}</span>}</div>}
                {b.phone && <div className="text-muted-foreground font-mono text-xs">{b.phone}</div>}
              </div>
            )}
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button asChild size="icon" variant="outline" className="h-8 w-8" title="Open profile">
                <Link to={`/app/branches/${b.id}`} data-testid={`open-branch-${b.id}`}><ArrowRight size={15} /></Link>
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`}><PencilSimple size={15} /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button size="icon" variant="outline" className="h-8 w-8 text-destructive" data-testid={`delete-branch-${b.id}`}><Trash size={15} /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete branch?</AlertDialogTitle><AlertDialogDescription>Remove {b.bank_name} — {b.branch_name}? This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(b.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">No branches match your filters.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Branch" : "Add Branch"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="branch-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bank Name *</Label><BankCombobox value={form.bank_name} onChange={(v) => setForm((f) => ({ ...f, bank_name: v }))} banks={bankList} testid="form-bank-name" /></div>
              <div className="space-y-1.5"><Label>Branch *</Label><Input required value={form.branch_name} onChange={set("branch_name")} data-testid="form-branch-name" /></div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>IFSC</Label><Input value={form.ifsc} onChange={set("ifsc")} data-testid="form-ifsc" /></div>
              <div className="space-y-1.5"><Label>Branch Code</Label><Input value={form.branch_code} onChange={set("branch_code")} data-testid="form-branch-code" /></div>
              <div className="space-y-1.5"><Label>Region / Zone</Label><Input value={form.region} onChange={set("region")} placeholder="Raipur City" data-testid="form-region" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bank Category</Label>
                <Select value={form.bank_category || "Unclassified"} onValueChange={set("bank_category")}><SelectTrigger data-testid="form-bank-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{BANK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Empanelment Status</Label>
                <Select value={form.empanelment_status || "not_empanelled"} onValueChange={set("empanelment_status")}><SelectTrigger data-testid="form-emp-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EMPANELMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Empanelment Date</Label><DatePicker value={(form.empanelment_date || "").slice(0, 10)} onChange={set("empanelment_date")} clearable placeholder="Select date" className="w-full" data-testid="form-emp-date" /></div>
              <div className="space-y-1.5"><Label>Renewal Due</Label><DatePicker value={(form.empanelment_renewal_due || "").slice(0, 10)} onChange={set("empanelment_renewal_due")} clearable placeholder="Select date" className="w-full" data-testid="form-emp-due" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact_person} onChange={set("contact_person")} /></div>
              <div className="space-y-1.5"><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="form-phone" /></div>
              <div className="space-y-1.5"><Label className="text-accent font-medium">Branch Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="branch@bank.com" data-testid="form-email" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Branch Email is used to deliver the "Email to Bank" valuation report link.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Stage</Label>
                <Select value={form.stage} onValueChange={set("stage")}><SelectTrigger data-testid="form-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Potential Value (₹)</Label><Input type="number" value={form.potential_value} onChange={set("potential_value")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Valuation Report Formats</Label>
              <div className="flex flex-wrap gap-2" data-testid="branch-formats">
                {formats.length === 0 && <p className="text-[11px] text-muted-foreground">No formats yet — create them in Valuation Data → Bank Report Formats.</p>}
                {formats.map((f) => {
                  const on = (form.report_format_ids || []).includes(f.id);
                  return <button type="button" key={f.id} onClick={() => toggleFormat(f.id)} data-testid={`branch-format-${f.id}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:border-primary"}`}>
                    {f.name} · {LAYOUT_LABELS[f.layout_key] || f.layout_key}</button>;
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">Select the report formats this branch accepts (e.g. Open Land, Land &amp; Building, Industrial Land, Flats). These appear when creating a valuation job for this branch.</p>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-branch-btn">{editing ? "Update" : "Add"} Branch</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tplTarget} onOpenChange={(v) => { if (!v) setTplTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Report Template</DialogTitle></DialogHeader>
          {tplTarget && (() => {
            const meta = tplMap[tplKey(tplTarget.bank, tplTarget.fmtId)];
            return (
              <div className="space-y-4" data-testid="bank-template-dialog">
                <div className="text-sm">
                  <div className="font-semibold text-primary">{tplTarget.bank}</div>
                  <div className="text-muted-foreground">{tplTarget.fmtName} · {tplTarget.layout}</div>
                  <p className="text-[11px] text-muted-foreground mt-1">A downloadable reference template for <strong>{tplTarget.layout}</strong> jobs of this bank. It appears as a download link inside each job (Bank tab &amp; Generate Report tab) and is shared across all branches of {tplTarget.bank}.</p>
                </div>
                {meta ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0"><Paperclip size={16} className="text-primary shrink-0" /><span className="text-sm truncate" title={meta.template_filename}>{meta.template_filename}</span></div>
                    <a href={`${API}/bank-templates/template?bank=${encodeURIComponent(tplTarget.bank)}&format_id=${tplTarget.fmtId}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline shrink-0" data-testid="bank-template-preview"><Eye size={14} /> Preview</a>
                  </div>
                ) : <p className="text-xs text-muted-foreground">No template attached yet.{!isOwner && " Ask the firm owner to attach it."}</p>}
                <input ref={tplFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" className="hidden" onChange={uploadTpl} data-testid="bank-template-input" />
                {isOwner && (
                <DialogFooter className="gap-2 sm:gap-2">
                  {meta && <Button type="button" variant="outline" className="rounded-full text-rose-600 hover:text-rose-700" disabled={tplBusy} onClick={removeTpl} data-testid="bank-template-remove"><Trash size={15} className="mr-1" />Remove</Button>}
                  <Button type="button" className="rounded-full bg-primary hover:bg-[#113A64]" disabled={tplBusy} onClick={() => tplFileRef.current?.click()} data-testid="bank-template-upload"><UploadSimple size={15} className="mr-1" />{tplBusy ? "Uploading…" : meta ? "Replace file" : "Attach file"}</Button>
                </DialogFooter>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
